/**
 * L1J Web Client - Character Creation Scene
 * 캐릭터 생성 화면 (Lineage 1 3.63C stats rules)
 *
 * Gateway 프로토콜:
 * - 전송: { type: 'new_char', payload: { name, type, sex, stats } }
 * - 수신: { type: 'char_create_result', payload: { result, message } }
 * - 성공 시 자동으로 LOGINTOSERVER로 게임 진입
 */

import Phaser from 'phaser';
import { ConnectionManager } from '../../network/ConnectionManager';

// 직업 정의 (L1J-JP 3.63C 기준)
// 각 직업의 초기 스탯과 추가 가능 포인트
interface ClassDefinition {
  id: number;
  name: string;
  nameKo: string;
  base: { str: number; dex: number; con: number; wis: number; cha: number; int: number };
  bonusPool: number;
  iconColor: string;
}

// 서버 C_CreateChar.java의 ORIGINAL_STR/DEX/CON/WIS/CHA/INT/AMOUNT 배열과 정확히 일치.
// 서버 type 순서: 0=Royal, 1=Knight, 2=Elf, 3=Wizard, 4=Dark Elf, 5=Dragon Knight, 6=Illusionist
// 각 직업의 base 합계 + bonusPool = 75 (서버 statusAmount 검증과 동일)
const CLASSES: ClassDefinition[] = [
  { id: 0, name: 'Royal',        nameKo: '군주',   base: { str: 13, dex: 10, con: 10, wis: 11, cha: 13, int: 10 }, bonusPool:  8, iconColor: '#c9a227' },
  { id: 1, name: 'Knight',       nameKo: '기사',   base: { str: 16, dex: 12, con: 14, wis:  9, cha: 12, int:  8 }, bonusPool:  4, iconColor: '#2266aa' },
  { id: 2, name: 'Elf',          nameKo: '요정',   base: { str: 11, dex: 12, con: 12, wis: 12, cha:  9, int: 12 }, bonusPool:  7, iconColor: '#22cc22' },
  { id: 3, name: 'Wizard',       nameKo: '마법사',  base: { str:  8, dex:  7, con: 12, wis: 12, cha:  8, int: 12 }, bonusPool: 16, iconColor: '#aa6622' },
  { id: 4, name: 'Dark Elf',     nameKo: '다크엘프', base: { str: 12, dex: 15, con:  8, wis: 10, cha:  9, int: 11 }, bonusPool: 10, iconColor: '#8822dd' },
  { id: 5, name: 'DragonKnight', nameKo: '용기사',  base: { str: 13, dex: 11, con: 14, wis: 12, cha:  8, int: 11 }, bonusPool:  6, iconColor: '#dd4422' },
  { id: 6, name: 'Illusionist',  nameKo: '환술사',  base: { str: 11, dex: 10, con: 12, wis: 12, cha:  8, int: 12 }, bonusPool: 10, iconColor: '#22dddd' },
];

interface CreateCharPayload {
  result: number;
  message: string;
}

export class CharCreateScene extends Phaser.Scene {
  private connectionManager!: ConnectionManager;

  // 입력 컨테이너 (DOM)
  private nameInput!: HTMLInputElement;
  private errorText!: HTMLElement;

  // 상태
  private selectedClass: number = 0; // 기본: 군주
  private selectedSex: number = 0;    // 0=male, 1=female
  private stats!: { str: number; dex: number; con: number; wis: number; cha: number; int: number };
  private bonusRemaining: number = 0;
  private statusTexts: Record<string, Phaser.GameObjects.Text> = {};
  private totalText!: Phaser.GameObjects.Text;
  private classButtons: Phaser.GameObjects.Text[] = [];
  private sexButtons: Phaser.GameObjects.Text[] = [];
  /** 게임 진입 신호(own_char_status)를 중복 처리하지 않기 위한 가드 */
  private enteredGame = false;
  /** ConnectionManager에 등록한 리스너 (shutdown 시 동일 참조로 해제) */
  private connectionListeners: Array<{ event: string; handler: (data: unknown) => void }> = [];

  constructor() {
    super({ key: 'CharCreateScene' });
    this.stats = { str: 0, dex: 0, con: 0, wis: 0, cha: 0, int: 0 };
  }

  create(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    this.cameras.main.setBackgroundColor(0x0a0a14);

    // 씬 재진입 시 이전 실행의 상태/파괴된 GameObject 참조가 남지 않도록 리셋
    this.enteredGame = false;
    this.classButtons = [];
    this.sexButtons = [];
    this.statusTexts = {};
    this.selectedClass = 0;
    this.selectedSex = 0;
    this.connectionListeners = [];

    this.connectionManager = ConnectionManager.getInstance();

    // 초기 직업의 기본 스탯으로 시작 (selectedClass와 동기화)
    this.applyClassBase(this.selectedClass);

    // 배경
    this.createBackground();

    // 타이틀
    this.add.text(width / 2, 40, 'CREATE NEW CHARACTER', {
      fontSize: '28px',
      fontFamily: 'Georgia, serif',
      color: '#c9a227',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);

    // 직업 선택
    this.createClassSelector(width / 2, 110);

    // 성별 선택
    this.createSexSelector(width / 2, 230);

    // 이름 입력
    this.createNameInput(width / 2, 300);

    // 스탯 분배
    this.createStatAllocator(width / 2, 410);

    // 액션 버튼
    this.createActionButtons(width / 2, height - 60);

    // 이벤트 리스너 등록 (shutdown 시 동일 참조로 해제하기 위해 추적)
    this.onNet('char_create_result', (data: unknown) => {
      this.handleCreateResult(data as CreateCharPayload);
    });

    // 게이트웨이는 char_spawn을 보내지 않는다 — 인게임 진입은
    // own_char_status(opcode 145)로 통지된다 (CharSelectScene과 동일 패턴).
    this.onNet('own_char_status', (data: unknown) => {
      this.handleEnterGame(data);
    });

    // Phaser는 shutdown() 메서드를 자동 호출하지 않으므로 이벤트로 정리 훅 등록
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanup, this);
  }

  /**
   * ConnectionManager 구독 + shutdown 시 해제를 위한 참조 추적 헬퍼.
   */
  private onNet(event: string, handler: (data: unknown) => void): void {
    this.connectionManager.on(event, handler);
    this.connectionListeners.push({ event, handler });
  }

  /**
   * own_char_status 수신 → 게임 진입. 주기적으로 재전송되는 신호이므로 1회만 처리.
   */
  private handleEnterGame(data: unknown): void {
    if (this.enteredGame) return;
    this.enteredGame = true;
    console.log('[CharCreateScene] own_char_status received → GameScene', data);
    this.scene.start('GameScene', { charData: data });
  }

  private createBackground(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a1a2e, 0x1a1a2e, 0x0f0f1a, 0x0f0f1a, 1);
    bg.fillRect(0, 0, width, height / 2);
    bg.fillGradientStyle(0x16213e, 0x16213e, 0x0a0a14, 0x0a0a14, 1);
    bg.fillRect(0, height / 2, width, height / 2);
  }

  private createClassSelector(centerX: number, y: number): void {
    const startX = centerX - (CLASSES.length * 90) / 2;
    CLASSES.forEach((cls, idx) => {
      const x = startX + idx * 90 + 40;
      const btn = this.add.text(x, y, cls.nameKo, {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#aaaaaa',
        backgroundColor: '#1a1a2e',
        padding: { x: 6, y: 6 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => { if (idx !== this.selectedClass) btn.setColor('#ffffff'); });
      btn.on('pointerout',  () => { if (idx !== this.selectedClass) btn.setColor('#aaaaaa'); });
      btn.on('pointerdown', () => {
        this.selectedClass = idx;
        this.refreshClassSelector();
        this.applyClassBase(idx);
      });

      this.classButtons.push(btn);
    });
    this.refreshClassSelector();
  }

  private refreshClassSelector(): void {
    this.classButtons.forEach((btn, idx) => {
      const selected = idx === this.selectedClass;
      btn.setColor(selected ? '#c9a227' : '#aaaaaa');
      btn.setStyle({ backgroundColor: selected ? '#3d3d5c' : '#1a1a2e' });
    });
  }

  private createSexSelector(centerX: number, y: number): void {
    this.add.text(centerX, y, 'SEX', { fontSize: '14px', color: '#8888aa' }).setOrigin(0.5);
    const labels = ['남', '여'];
    labels.forEach((label, idx) => {
      const x = centerX - 60 + idx * 120;
      const btn = this.add.text(x, y + 28, label, {
        fontSize: '18px',
        color: '#aaaaaa',
        backgroundColor: '#1a1a2e',
        padding: { x: 14, y: 6 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.selectedSex = idx;
        this.refreshSexSelector();
      });
      this.sexButtons.push(btn);
    });
    this.refreshSexSelector();
  }

  private refreshSexSelector(): void {
    this.sexButtons.forEach((btn, idx) => {
      const selected = idx === this.selectedSex;
      btn.setColor(selected ? '#c9a227' : '#aaaaaa');
      btn.setStyle({ backgroundColor: selected ? '#3d3d5c' : '#1a1a2e' });
    });
  }

  private createNameInput(centerX: number, y: number): void {
    this.add.text(centerX - 130, y, 'NAME', { fontSize: '14px', color: '#8888aa' }).setOrigin(0, 0.5);
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 12;
    input.placeholder = '캐릭터 이름 (한글/영문/숫자)';
    input.style.cssText = `
      position: absolute; left: ${centerX - 80}px; top: ${y - 12}px;
      width: 280px; height: 28px; padding: 4px 8px;
      background: #0a0a14; color: #ffffff; border: 1px solid #3d3d5c;
      font-size: 14px; font-family: Arial;
    `;
    document.body.appendChild(input);
    this.nameInput = input;

    this.errorText = document.createElement('div');
    this.errorText.style.cssText = `
      position: absolute; left: ${centerX - 200}px; top: ${y + 30}px;
      width: 400px; text-align: center; color: #ff6666;
      font-size: 13px; font-family: Arial;
    `;
    this.errorText.textContent = '';
    document.body.appendChild(this.errorText);
  }

  private applyClassBase(classIdx: number): void {
    const cls = CLASSES[classIdx];
    this.stats = { ...cls.base };
    this.bonusRemaining = cls.bonusPool;
    this.refreshStatUI();
  }

  private createStatAllocator(centerX: number, y: number): void {
    this.add.text(centerX, y - 20, 'STATS (총합 75, 보너스 분배 가능)', {
      fontSize: '14px', color: '#8888aa',
    }).setOrigin(0.5);

    const statNames: { key: string; label: string }[] = [
      { key: 'str', label: 'STR (힘)' },
      { key: 'dex', label: 'DEX (민첩)' },
      { key: 'con', label: 'CON (체력)' },
      { key: 'wis', label: 'WIS (마력)' },
      { key: 'cha', label: 'CHA (매력)' },
      { key: 'int', label: 'INT (지력)' },
    ];

    statNames.forEach((s, idx) => {
      const row = y + idx * 22;
      this.add.text(centerX - 200, row, s.label, {
        fontSize: '14px', color: '#cccccc',
      }).setOrigin(0, 0.5);

      const minus = this.add.text(centerX - 40, row, '−', {
        fontSize: '18px', color: '#888888',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      minus.on('pointerdown', () => this.adjustStat(s.key, -1));

      const valueText = this.add.text(centerX, row, '0', {
        fontSize: '16px', color: '#ffffff', fontStyle: 'bold',
      }).setOrigin(0.5);

      const plus = this.add.text(centerX + 40, row, '+', {
        fontSize: '18px', color: '#888888',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      plus.on('pointerdown', () => this.adjustStat(s.key, 1));

      this.statusTexts[s.key] = valueText;
    });

    const totalY = y + statNames.length * 22 + 10;
    this.totalText = this.add.text(centerX, totalY, '합계: 0/75   분배 가능: 0', {
      fontSize: '14px', color: '#88aadd',
    }).setOrigin(0.5);
  }

  private adjustStat(key: string, delta: number): void {
    if (delta > 0 && this.bonusRemaining <= 0) return;
    // 서버의 C_CreateChar.isStatusError 검증을 통과하려면 각 스탯이
    // 선택된 클래스의 base 값 이상이어야 한다.
    // (ORIGINAL_STR[type]..ORIGINAL_INT[type]가 클래스별 최소값)
    const minVal = CLASSES[this.selectedClass].base[key];
    const newVal = this.stats[key] + delta;
    if (newVal < minVal || newVal > 35) return;
    this.stats[key] = newVal;
    this.bonusRemaining -= delta;
    this.refreshStatUI();
  }

  private refreshStatUI(): void {
    const statOrder: (keyof typeof this.stats)[] = ['str', 'dex', 'con', 'wis', 'cha', 'int'];
    statOrder.forEach(k => {
      if (this.statusTexts[k]) this.statusTexts[k].setText(String(this.stats[k]));
    });
    const total = statOrder.reduce((s, k) => s + this.stats[k], 0);
    if (this.totalText) {
      this.totalText.setText(`합계: ${total}/75   분배 가능: ${this.bonusRemaining}`);
    }
  }

  private createActionButtons(centerX: number, y: number): void {
    const ok = this.add.text(centerX - 100, y, '[ CREATE ]', {
      fontSize: '20px', color: '#c9a227', backgroundColor: '#1a1a2e',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    ok.on('pointerover', () => ok.setColor('#ffdd44'));
    ok.on('pointerout',  () => ok.setColor('#c9a227'));
    ok.on('pointerdown', () => this.submitCreate());

    const cancel = this.add.text(centerX + 100, y, '[ CANCEL ]', {
      fontSize: '20px', color: '#888888', backgroundColor: '#1a1a2e',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    cancel.on('pointerover', () => cancel.setColor('#ffffff'));
    cancel.on('pointerout',  () => cancel.setColor('#888888'));
    cancel.on('pointerdown', () => {
      // 씬 전환 시 shutdown 이벤트에서 cleanup()이 자동 실행된다.
      this.scene.start('CharSelectScene');
    });
  }

  private submitCreate(): void {
    const name = this.nameInput.value.trim();
    if (!name) {
      this.showError('이름을 입력하세요.');
      return;
    }
    const statOrder: (keyof typeof this.stats)[] = ['str', 'dex', 'con', 'wis', 'cha', 'int'];
    const total = statOrder.reduce((s, k) => s + this.stats[k], 0);
    if (total !== 75) {
      this.showError(`총 스탯이 75가 아닙니다 (현재: ${total}).`);
      return;
    }
    if (this.bonusRemaining !== 0) {
      this.showError(`분배 가능한 보너스 스탯이 ${this.bonusRemaining} 남았습니다.`);
      return;
    }
    // 서버측 isStatusError 검증과 동일한 클라이언트 측 방어적 검사.
    // 각 스탯이 선택된 클래스의 base 값 이상이어야 한다.
    const base = CLASSES[this.selectedClass].base;
    const underBase: string[] = [];
    for (const k of statOrder) {
      if (this.stats[k] < base[k]) {
        underBase.push(`${k.toUpperCase()}(${this.stats[k]}<${base[k]})`);
      }
    }
    if (underBase.length > 0) {
      this.showError(`다음 스탯이 클래스 기본값보다 작습니다: ${underBase.join(', ')}`);
      return;
    }

    const stats = [this.stats.str, this.stats.dex, this.stats.con, this.stats.wis, this.stats.cha, this.stats.int];
    console.log('[CharCreateScene] Submitting:', { name, type: this.selectedClass, sex: this.selectedSex, stats });

    this.connectionManager.send({
      type: 'new_char',
      payload: {
        name,
        type: this.selectedClass,
        sex: this.selectedSex,
        stats,
      },
    });
  }

  private handleCreateResult(payload: CreateCharPayload): void {
    console.log('[CharCreateScene] Create result:', payload);
    if (payload.result === 0) {
      this.showError('캐릭터 생성 성공! 게임으로 진입합니다...', '#88dd88');
      // select_char 전송 후 게이트웨이가 own_char_status를 보내면
      // handleEnterGame()이 GameScene으로 전환한다.
      const createdName = this.nameInput.value.trim();
      try {
        const stored = JSON.parse(localStorage.getItem('l1j_chars') || '[]');
        if (!stored.includes(createdName)) {
          stored.push(createdName);
          localStorage.setItem('l1j_chars', JSON.stringify(stored));
        }
      } catch (e) { /* ignore */ }
      this.connectionManager.send({
        type: 'select_char',
        payload: { charName: createdName },
      });
    } else {
      this.showError(`생성 실패: ${payload.message}`);
    }
  }

  private showError(msg: string, color = '#ff6666'): void {
    if (this.errorText) {
      this.errorText.style.color = color;
      this.errorText.textContent = msg;
    }
  }

  private cleanupDom(): void {
    if (this.nameInput && this.nameInput.parentNode) this.nameInput.parentNode.removeChild(this.nameInput);
    if (this.errorText && this.errorText.parentNode) this.errorText.parentNode.removeChild(this.errorText);
  }

  /**
   * 씬 종료(shutdown 이벤트) 시 정리.
   * 등록 시 보관한 동일 핸들러 참조로 off()해야 실제로 해제된다.
   */
  private cleanup(): void {
    this.cleanupDom();
    this.connectionListeners.forEach(({ event, handler }) => {
      this.connectionManager.off(event, handler);
    });
    this.connectionListeners = [];
  }
}